import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../api';
import { flikenArDold } from '../utils/usePolling';

const WSContext = createContext(null);

// A11 (2026-10-07): varje öppen anslutning räknas mot uvicorns
// --limit-concurrency (bekräftat i dev: 503 från 49 öppna anslutningar).
// Flikar som varit dolda i 10 min släpper därför anslutningen och återansluter
// när fliken visas igen. Räknarna hämtas då om av usePolling i skalet.
const STANG_EFTER_DOLD_MS = 10 * 60 * 1000;
// Backoff nollställs först när anslutningen hållit så här länge, så att en
// server som accepterar och genast stänger inte ger en återförsöksstorm.
const STABIL_EFTER_MS = 30 * 1000;

// Hälsofrågan (/communication/health) ställs en gång per sidladdning, inte
// vid varje återanslutning.
let halsaKontrollerad = false;
let funktionenAvstangd = false;
async function kontrolleraHalsaEnGang() {
  if (halsaKontrollerad) return funktionenAvstangd;
  try {
    const healthRes = await fetch(`${API_URL}/communication/health`, { credentials: 'include' });
    if (healthRes.ok) {
      const data = await healthRes.json();
      halsaKontrollerad = true;
      if (data && data.enabled === false) funktionenAvstangd = true;
    }
  } catch {
    // Nätverksfel — försök ansluta ändå; frågan ställs om nästa gång.
  }
  return funktionenAvstangd;
}

function getTenantSlugSafe() {
  try {
    return String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim();
  } catch {
    return '';
  }
}

function buildWsUrl(params = {}) {
  const loc = window.location;
  const proto = loc.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = API_URL.replace(/^http(s?):\/\//, '');
  const usp = new URLSearchParams(params);
  try {
    const slug = localStorage.getItem('kanplan-active-tenant-slug');
    if (slug && String(slug).trim()) usp.set('tenant_slug', String(slug).trim());
  } catch {}
  return `${proto}//${host}/communication/ws?${usp.toString()}`;
}

/**
 * WebSocket provider (single shared connection) with dynamic topic subscriptions.
 * Props:
 * - userId?: number (optional, used for dev auth fallback via query param)
 * - initial?: { projectId?: number|string, directKey?: string, inboxUserId?: number|string } (optional initial subs)
 */
export function WSProvider({ children, userId, initial }) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const reconnectTimerRef = useRef(null);
  const intentionallyClosed = useRef(false);
  const topicsRef = useRef(new Set());
  const connectRef = useRef(null);
  const featureDisabledRef = useRef(false);
  const ansluterRef = useRef(false);
  const stabilTimerRef = useRef(null);
  const doldTimerRef = useRef(null);
  const pausadDoldRef = useRef(false);

  // Helpers to send control messages
  const sendJson = useCallback((obj) => {
    const ws = wsRef.current;
    // readyState 1 === WebSocket.OPEN; guard to avoid sending on closing/closed sockets
    if (!ws || ws.readyState !== 1) return;
    try { ws.send(JSON.stringify(obj)); } catch {}
  }, []);

  const subscribe = useCallback((topic) => {
    if (!topic) return;
    topicsRef.current.add(String(topic));
    // If we're currently idle, subscribing should bring the connection up.
    try { connectRef.current?.(); } catch {}
    sendJson({ action: 'subscribe', topic: String(topic) });
  }, [sendJson]);

  const unsubscribe = useCallback((topic) => {
    if (!topic) return;
    topicsRef.current.delete(String(topic));
    sendJson({ action: 'unsubscribe', topic: String(topic) });

    // If no topics remain, close the socket to avoid background reconnect noise.
    try {
      if (topicsRef.current.size === 0) {
        const ws = wsRef.current;
        if (ws && (ws.readyState === 0 || ws.readyState === 1)) {
          try { ws.close(); } catch {}
        }
      }
    } catch {}
  }, [sendJson]);

  useEffect(() => {
    intentionallyClosed.current = false;

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const scheduleReconnect = (delayMs) => {
      if (intentionallyClosed.current) return;
      clearReconnectTimer();
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delayMs);
    };

    async function connect() {
      if (intentionallyClosed.current) return;

      // If we already know the feature is disabled server-side, don't attempt.
      if (featureDisabledRef.current) return;

      // Lazy connection: only connect when someone has subscribed.
      if (topicsRef.current.size === 0) {
        setConnected(false);
        return;
      }

      // Avoid stacking multiple connect attempts.
      const existing = wsRef.current;
      if (existing && (existing.readyState === 0 || existing.readyState === 1)) {
        return;
      }
      // ...även medan hälsofrågan nedan väntar (kapplöpning: en prenumeration
      // som kommer in under await:en skapade tidigare en andra anslutning).
      if (ansluterRef.current) return;

      // Dold flik som passerat gränsen: vänta med anslutningen tills den visas.
      if (pausadDoldRef.current) return;

      // Avoid noisy connection attempts until a tenant is selected (or dev inbox_user fallback is used).
      // Without tenant_slug, the backend may close immediately and the browser will log errors.
      const tenantSlug = getTenantSlugSafe();
      if (!tenantSlug && !userId) {
        setConnected(false);
        wsRef.current = null;
		scheduleReconnect(2000);
        return;
      }

      // Check if the communication feature is enabled on the server before connecting.
      ansluterRef.current = true;
      let avstangd = false;
      try {
        avstangd = await kontrolleraHalsaEnGang();
      } finally {
        ansluterRef.current = false;
      }
      if (avstangd) {
        featureDisabledRef.current = true;
        setConnected(false);
        return;
      }

      if (intentionallyClosed.current || pausadDoldRef.current) return;
      if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;

      const params = {};
      // Only pass inbox_user for dev fallback auth; topics are managed dynamically
      if (userId) params.inbox_user = userId;
      const url = buildWsUrl(params);
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          setConnected(true);
          clearReconnectTimer();
          // Nollställ backoff först efter en stabil period.
          if (stabilTimerRef.current) clearTimeout(stabilTimerRef.current);
          stabilTimerRef.current = setTimeout(() => {
            stabilTimerRef.current = null;
            if (wsRef.current === ws && ws.readyState === 1) backoffRef.current = 1000;
          }, STABIL_EFTER_MS);
          // Resubscribe to all known topics on reconnect
          try {
            for (const t of topicsRef.current) {
              sendJson({ action: 'subscribe', topic: t });
            }
          } catch {}
          // Apply any initial subscriptions passed as props (once)
          try {
            if (initial) {
              const initTopics = [];
              if (initial.projectId) initTopics.push(`project:${initial.projectId}`);
              if (initial.directKey) initTopics.push(`direct:${initial.directKey}`);
              if (initial.inboxUserId || userId) initTopics.push(`inbox:${initial.inboxUserId || userId}`);
              initTopics.forEach(t => { topicsRef.current.add(t); sendJson({ action: 'subscribe', topic: t }); });
            }
          } catch {}
        };
        ws.onmessage = (ev) => {
          try { setLastEvent(JSON.parse(ev.data)); } catch { /* ignore */ }
        };
        ws.onclose = () => {
          setConnected(false);
          if (wsRef.current === ws) wsRef.current = null;
          if (stabilTimerRef.current) { clearTimeout(stabilTimerRef.current); stabilTimerRef.current = null; }
          if (!intentionallyClosed.current && !pausadDoldRef.current && topicsRef.current.size > 0) {
            const delay = Math.min(10000, backoffRef.current * 1.7 + Math.random() * 500);
            backoffRef.current = delay;
			scheduleReconnect(delay);
          }
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        setConnected(false);
		scheduleReconnect(Math.min(10000, backoffRef.current * 1.7));
      }
    }

    // Dold flik: släpp anslutningen efter STANG_EFTER_DOLD_MS, återanslut vid återkomst.
    const clearDoldTimer = () => {
      if (doldTimerRef.current) {
        clearTimeout(doldTimerRef.current);
        doldTimerRef.current = null;
      }
    };
    const onVisibility = () => {
      if (flikenArDold()) {
        clearDoldTimer();
        doldTimerRef.current = setTimeout(() => {
          doldTimerRef.current = null;
          if (!flikenArDold()) return;
          pausadDoldRef.current = true;
          clearReconnectTimer();
          const ws = wsRef.current;
          if (ws && (ws.readyState === 0 || ws.readyState === 1)) {
            try { ws.close(); } catch {}
          }
          setConnected(false);
        }, STANG_EFTER_DOLD_MS);
      } else {
        clearDoldTimer();
        if (pausadDoldRef.current) {
          pausadDoldRef.current = false;
          backoffRef.current = 1000;
          connect();
        }
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
      if (flikenArDold()) onVisibility();
    }

    connectRef.current = connect;
    connect();
    return () => {
      intentionallyClosed.current = true;
      clearReconnectTimer();
      clearDoldTimer();
      if (stabilTimerRef.current) { clearTimeout(stabilTimerRef.current); stabilTimerRef.current = null; }
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      try { wsRef.current?.close(); } catch {}
      wsRef.current = null;
    };
  }, [userId, initial, sendJson]);

  // Convenience helpers for common topics
  const subscribeInbox = useCallback((userIdOrStr) => subscribe(`inbox:${userIdOrStr}`), [subscribe]);
  const unsubscribeInbox = useCallback((userIdOrStr) => unsubscribe(`inbox:${userIdOrStr}`), [unsubscribe]);
  const subscribeProject = useCallback((pid) => subscribe(`project:${pid}`), [subscribe]);
  const unsubscribeProject = useCallback((pid) => unsubscribe(`project:${pid}`), [unsubscribe]);
  const subscribeDirect = useCallback((key) => subscribe(`direct:${key}`), [subscribe]);
  const unsubscribeDirect = useCallback((key) => unsubscribe(`direct:${key}`), [unsubscribe]);

  const value = useMemo(() => ({
    connected,
    lastEvent,
    subscribe,
    unsubscribe,
    subscribeInbox,
    unsubscribeInbox,
    subscribeProject,
    unsubscribeProject,
    subscribeDirect,
    unsubscribeDirect,
  }), [connected, lastEvent, subscribe, unsubscribe, subscribeInbox, unsubscribeInbox, subscribeProject, unsubscribeProject, subscribeDirect, unsubscribeDirect]);
  return <WSContext.Provider value={value}>{children}</WSContext.Provider>;
}

export function useWS() {
  return useContext(WSContext);
}
